import { GOOGLE_MAPS_CONFIG } from '../config/googleMaps';

const GOOGLE_MAPS_SCRIPT_ERROR = 'The Google Maps JavaScript API could not load.';

let googleMapsLoadPromise = null;

const hasGoogleMapsImportLibrary = () => (
  typeof window !== 'undefined' &&
  Boolean(window.google?.maps?.importLibrary)
);

const bootstrapGoogleMapsImportLibrary = (config = GOOGLE_MAPS_CONFIG) => {
  const googleNamespace = window.google || (window.google = {});
  const mapsNamespace = googleNamespace.maps || (googleNamespace.maps = {});

  if (mapsNamespace.importLibrary) {
    return mapsNamespace.importLibrary;
  }

  let bootstrapPromise;
  let scriptElement;
  let bootstrapKey;
  const requestedLibraries = new Set();
  const params = new URLSearchParams();
  const callbackName = '__ib__';

  const loadScript = () => (
    bootstrapPromise || (bootstrapPromise = new Promise((resolve, reject) => {
      scriptElement = document.createElement('script');
      params.set('libraries', [...requestedLibraries].join(','));

      for (bootstrapKey in config) {
        params.set(
          bootstrapKey.replace(/[A-Z]/g, (token) => `_${token[0].toLowerCase()}`),
          config[bootstrapKey]
        );
      }

      params.set('callback', `google.maps.${callbackName}`);
      scriptElement.src = `https://maps.googleapis.com/maps/api/js?${params}`;
      scriptElement.async = true;
      scriptElement.defer = true;
      scriptElement.setAttribute('data-ns-address-lookup-google-maps', 'true');
      mapsNamespace[callbackName] = resolve;
      scriptElement.onerror = () => {
        bootstrapPromise = null;
        googleMapsLoadPromise = null;
        delete mapsNamespace[callbackName];
        delete mapsNamespace.importLibrary;
        scriptElement.remove();
        reject(new Error(GOOGLE_MAPS_SCRIPT_ERROR));
      };
      scriptElement.nonce = document.querySelector('script[nonce]')?.nonce || '';
      document.head.append(scriptElement);
    }))
  );

  mapsNamespace.importLibrary = (libraryName, ...args) => (
    requestedLibraries.add(libraryName) &&
    loadScript().then(() => mapsNamespace.importLibrary(libraryName, ...args))
  );

  return mapsNamespace.importLibrary;
};

export const ensureGoogleMapsLoaded = async () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  if (!googleMapsLoadPromise) {
    const importLibrary = hasGoogleMapsImportLibrary() ?
      window.google.maps.importLibrary :
      bootstrapGoogleMapsImportLibrary();

    googleMapsLoadPromise = importLibrary('maps').catch((error) => {
      googleMapsLoadPromise = null;
      throw error;
    });
  }

  await googleMapsLoadPromise;
  return true;
};
